import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import BottleSilhouette from "../components/BottleSilhouette";
import { api } from "../lib/api";
import { CATEGORY_COLORS } from "../lib/constants";

const CATEGORY_LABELS = {
  dna: "DNA Series",
  country: "Country Series",
  mood: "Mood Series",
  education: "Education Series",
  premium: "Collector Series",
};

const CATEGORY_ORDER = ["dna", "country", "mood", "education", "premium"];

export default function Flights() {
  const [flights, setFlights] = useState([]);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/flights").then((r) => { setFlights(r.data.flights); setLoading(false); });
  }, []);

  const filtered = active === "all" ? flights : flights.filter((f) => f.category === active);
  const groups = CATEGORY_ORDER.map((cat) => ({
    cat,
    label: CATEGORY_LABELS[cat],
    items: flights.filter((f) => f.category === cat),
  }));

  return (
    <div className="px-5 md:px-12 max-w-7xl mx-auto pb-24" data-testid="flights-page">
      {/* Header */}
      <div className="py-10 border-b border-white/5 relative">
        <p className="overline text-[#C17F59] mb-3 flex items-center gap-3">
          <span className="font-hand normal-case tracking-normal text-base text-[#D89A74]">est. BENO</span>
          <span className="h-px w-10 bg-[#C17F59]/40" />
          Tasting Flights
        </p>
        <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[0.95] max-w-4xl">
          A guided gin journey, in <em className="not-italic text-[#C17F59]">three small pours.</em>
        </h1>
        <p className="text-white/65 max-w-2xl mt-4 leading-relaxed">
          Twelve curated flights from BENO's bartenders. Each is 3 × 15ml — a way to taste the difference,
          not just choose a side. Built around DNA, country, mood, education, and the collector's shelf.
        </p>
        <p className="font-hand text-2xl text-[#D89A74] mt-6 -rotate-1 inline-block">
          — pour slowly, take notes, talk less.
        </p>
      </div>

      {/* Category tabs */}
      <div className="py-5 flex flex-wrap gap-1 border-b border-white/5 overflow-x-auto no-scrollbar">
        {[{ k: "all", l: "All Flights" }, ...CATEGORY_ORDER.map((c) => ({ k: c, l: CATEGORY_LABELS[c] }))]
          .map((t) => (
            <button
              key={t.k}
              onClick={() => setActive(t.k)}
              className={`text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors ${
                active === t.k ? "text-[#C17F59] border-b border-[#C17F59]" : "text-white/50 hover:text-white"
              }`}
              data-testid={`flight-cat-${t.k}`}
            >
              {t.l}
            </button>
          ))}
      </div>

      {loading && <p className="py-20 text-white/40">Pouring...</p>}

      {/* Single category: flat list */}
      {active !== "all" && !loading && (
        <div className="py-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((f, i) => <FlightCard key={f.id} f={f} index={i} />)}
        </div>
      )}

      {/* All: grouped editorial layout */}
      {active === "all" && !loading && groups.map((g, gi) => (
        <section key={g.cat} className="py-12 border-b border-white/5" data-testid={`flight-group-${g.cat}`}>
          <div className="flex items-end justify-between mb-7">
            <div>
              <p className="overline text-white/40 mb-1">0{gi + 1}</p>
              <h2 className="font-display text-3xl sm:text-4xl">{g.label}</h2>
            </div>
            <span className="text-xs uppercase tracking-[0.25em] text-white/40">{g.items.length} flights</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {g.items.map((f, i) => <FlightCard key={f.id} f={f} index={i} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function FlightCard({ f, index }) {
  const color = CATEGORY_COLORS[f.color_key] || CATEGORY_COLORS.classic;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.4), duration: 0.5 }}
      className="group relative bg-[#15171A] border border-white/8 hover:border-white/25 transition-all duration-500"
      data-testid={`flight-card-${f.id}`}
    >
      <Link to={`/gin/flights/${f.id}`} className="block p-6 md:p-7">
        {/* Bartender note - hand-written annotation */}
        <p className="font-hand text-[#D89A74] text-base mb-3 -rotate-[0.5deg]">
          ~ {f.bartender}'s pour
        </p>

        <div className="flex items-start justify-between mb-4">
          <p className="overline" style={{ color }}>{f.kicker}</p>
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">{f.pours}</span>
        </div>

        <h3 className="font-display text-3xl leading-tight mb-3">{f.name}</h3>
        <p className="text-sm text-white/65 leading-relaxed mb-6 min-h-[60px]">{f.blurb}</p>

        {/* 3 bottle silhouettes lined up */}
        <div className="flex items-end justify-center gap-3 py-4 border-y border-white/5 relative">
          <div className="absolute inset-0 opacity-20"
               style={{ background: `radial-gradient(ellipse at center, ${color}22, transparent 70%)` }} />
          {f.gin_previews.map((g, i) => (
            <div key={g.id} className="relative flex flex-col items-center" style={{ transform: `translateY(${i === 1 ? "-8px" : "0"})` }}>
              <span className="absolute -top-2 -left-1 font-display text-[10px] text-white/40">0{i + 1}</span>
              <BottleSilhouette category={g.category} size={50} />
              <p className="text-[9px] uppercase tracking-[0.18em] text-white/50 mt-1 text-center max-w-[80px] leading-tight">
                {g.name}
              </p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-5">
          <div>
            <p className="overline text-white/40">Flight Price</p>
            <p className="font-display text-2xl">₹{f.price}</p>
          </div>
          <ArrowRight size={16} className="text-white/40 group-hover:text-[#C17F59] group-hover:translate-x-1 transition-all" />
        </div>
      </Link>
    </motion.div>
  );
}
